/** @prettier */

import React, {useEffect, useState} from 'react';
import {Grid, IconButton, Box, Stack} from '@mui/material';
import dayjs from 'dayjs';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import {DatePicker, LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {utcToZonedTime} from 'date-fns-tz';
import {useTranslation} from 'react-i18next';
import {addMonths, addYears, startOfMonth, subDays, subMonths} from 'date-fns';

//MTH component import
import {CalenderEditDialog} from './CalenderEditDialog';
//interface import
import * as apiIF from '../../Type/api';

import {iconBlack, iconWhite} from '../../define';
import {dayOfWeekTable, monthTable, weekNumberTable} from './ScheduleSetting';
import {getSettingUser} from '../../API/API_Collection';
import {decideDayjsDatePickerLocale} from '../../functions/decideDayjsDatePickerLocale';

/**
 * 月間カレンダー
 * @param props props
 * - siteID サイトID
 * - timeZone タイムゾーン
 * - calenderData カレンダーデータ
 * - calenderRoutineData 標準設定のデータ
 * - setHasCalenderInfoChanged カレンダー設定変更フラグ
 * @returns JSX
 */
export const Calender = (props: {
  siteID: string;
  timeZone: string;
  calenderData: apiIF.res.getSiteCalender;
  calenderRoutineData: apiIF.res.getSiteCalenderRoutine;
  setHasCalenderInfoChanged: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const trans = useTranslation().t; //翻訳関数

  /** 現在日 */
  const now = utcToZonedTime(new Date(), props.timeZone);

  /** データピッカーで選択状態の年月 */
  const [datePickerValue, setDatePickerValue] = useState(now);
  /** 編集ダイアログ開閉 */
  const [isOpenEditDialog, setIsOpenEditDialog] = useState(false);
  /** 言語情報 */
  const [language, setLanguage] =
    useState<apiIF.res.getSettingUser['language']>('english');

  /** 今月 */
  const thisMonth = now.getMonth();
  /** 今年 */
  const thisYear = now.getFullYear();
  /** カレンダーで選択可能な範囲の開始日(現在日の月の月初日) */
  const minCalendarDate = startOfMonth(now);
  /** カレンダーで選択可能な範囲の終了日(カレンダー開始日の1年後の1日前) */
  const maxCalendarDate = subDays(addYears(minCalendarDate, 1), 1);
  /** 選択月の日数 */
  const daysInMonth = new Date(
    datePickerValue.getFullYear(),
    datePickerValue.getMonth() + 1,
    0
  ).getDate();
  /** 選択月の開始日の曜日 */
  const firstDay = new Date(
    datePickerValue.getFullYear(),
    datePickerValue.getMonth(),
    1
  ).getDay();
  /** 月の名前（年間スケジュールのキー参照用） */
  const month = monthTable[datePickerValue.getMonth()];
  /** カレンダー日数カウント */
  let dateCount = 0;

  //言語切り替え時、言語情報更新
  useEffect(() => {
    getSettingUser().then(response => {
      setLanguage(response.language);
    });
  }, [trans]);

  /**
   * 開始日の曜日をカレンダー表示用に変換する（０：月曜日～６：日曜日）
   * @param firstDay 選択月の開始日の曜日
   * @returns （０：月曜日～６：日曜日）に変換した開始日の曜日
   */
  const changeFirstDay = (firstDay: number) => {
    if (firstDay === 0) {
      firstDay = 6;
    } else {
      firstDay = firstDay - 1;
    }
    return firstDay;
  };

  /**
   * 日付選択時の処理
   * @param date カレンダーで選択した日付
   */
  const dateChange = (date: dayjs.Dayjs | null) => {
    //日付がおかしい場合
    if (date === null || date.isValid() === false) return;
    /** dayjsオブジェクトをdateオブジェクトに変換 */
    const calendarDate = date.toDate();
    setDatePickerValue(calendarDate);
  };

  return (
    <Stack spacing={3}>
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center">
        {/** カレンダー変更ボタン */}
        <Grid item xs container alignItems="center">
          <Grid item xs="auto">
            {/** 左側のボタン */}
            <IconButton
              aria-label="calender-left-button"
              color="inherit"
              size="small"
              disabled={
                thisMonth === datePickerValue.getMonth() &&
                thisYear === datePickerValue.getFullYear()
              }
              onClick={e => {
                setDatePickerValue(subMonths(datePickerValue, 1));
              }}>
              <ArrowLeftIcon fontSize="large" />
            </IconButton>
          </Grid>
          <Grid item xs="auto">
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale={decideDayjsDatePickerLocale(language)}>
              <DatePicker
                views={['year', 'month']}
                value={dayjs(datePickerValue)}
                format={'MMMM/YYYY'}
                onChange={date => dateChange(date)}
                minDate={dayjs(minCalendarDate)}
                maxDate={dayjs(maxCalendarDate)}
                slotProps={{
                  textField: {size: 'small'},
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs>
            {/** 右側のボタン */}
            <IconButton
              aria-label="calender-right-button"
              color="inherit"
              size="small"
              disabled={
                // prettier-ignore
                (thisMonth === 0 ? 11 : thisMonth - 1) === datePickerValue.getMonth() &&
                  thisYear <= datePickerValue.getFullYear()
              }
              onClick={e => {
                setDatePickerValue(addMonths(datePickerValue, 1));
              }}>
              <ArrowRightIcon fontSize="large" />
            </IconButton>
          </Grid>
        </Grid>
        {/** 年スケジュール編集ボタン */}
        <Grid item xs="auto">
          <IconButton size="medium" onClick={() => setIsOpenEditDialog(true)}>
            <Box className="schedule__yearSchedule__editIcon__border">
              <img
                src={iconBlack.EDIT.src}
                alt={`${trans(
                  `Calender.image.alternativeText.${iconBlack.EDIT.alt}`
                )}`}
                title={`${trans(
                  `Calender.image.title.${iconWhite.EDIT.title}`
                )}`}
                className="schedule__yearSchedule__editIcon__border__image"
              />
            </Box>
          </IconButton>
        </Grid>
      </Grid>
      {/** 月カレンダー */}
      <table className="schedule__yearSchedule__calender">
        <thead>
          <tr>
            {dayOfWeekTable.map(dayOfWeek => {
              return (
                <th
                  key={dayOfWeek}
                  className="schedule__yearSchedule__calender__header__cell">
                  {trans(`Calender.dayOfWeek.${dayOfWeek}`)}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {/** 1か月の最大週数6でループ */}
          {weekNumberTable.map(weekNumber => {
            //月の日数を超えたら次週は表示しない
            if (dateCount >= daysInMonth) {
              return <React.Fragment key={weekNumber} />;
            }
            return (
              <tr key={weekNumber}>
                {/** 月曜日～日曜日でループ */}
                {dayOfWeekTable.map((dayOfWeek, dayOfWeekNumber) => {
                  //月始めの日までと月終わり日以降は空欄
                  if (
                    (weekNumber === 'first' &&
                      dayOfWeekNumber < changeFirstDay(firstDay)) ||
                    dateCount >= daysInMonth
                  ) {
                    return (
                      <td
                        key={dayOfWeek}
                        className="schedule__yearSchedule__calender__body__row__cell--notCurrentMonth"
                      />
                    );
                  }
                  //日数を一つ進める
                  dateCount++;
                  if (props.calenderData[month][dateCount - 1] === undefined) {
                    return (
                      <td
                        key={dayOfWeek}
                        className="schedule__yearSchedule__calender__body__row__cell--notCurrentMonth"
                      />
                    );
                  }
                  return (
                    <DateCell
                      key={dayOfWeek}
                      date={dateCount}
                      dayType={props.calenderData[month][dateCount - 1]}
                    />
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      {/* 年スケジュール編集ダイアログ */}
      <CalenderEditDialog
        timeZone={props.timeZone}
        calender={props.calenderData}
        calenderRoutine={props.calenderRoutineData}
        isOpenDialog={isOpenEditDialog}
        setIsOpenDialog={setIsOpenEditDialog}
        setHasCalenderInfoChanged={props.setHasCalenderInfoChanged}
      />
    </Stack>
  );
};

/**
 * 日付セルの表示設定
 * @param date 日付
 * @param dayType 日付タイプ
 * @returns 日付セル
 */
const DateCell = (props: {
  date: number;
  dayType: 'weekday' | 'holiday' | 'special-day-1' | 'special-day-2';
}) => {
  /** 適用するクラス名 */
  const className = ((
    dayType: 'weekday' | 'holiday' | 'special-day-1' | 'special-day-2'
  ) => {
    switch (dayType) {
      case 'weekday':
        return 'schedule__yearSchedule__calender__body__row__cell--weekday';
      case 'holiday':
        return 'schedule__yearSchedule__calender__body__row__cell--holiday';
      case 'special-day-1':
        return 'schedule__yearSchedule__calender__body__row__cell--specialDay1';
      case 'special-day-2':
        return 'schedule__yearSchedule__calender__body__row__cell--specialDay2';
    }
  })(props.dayType);

  return <td className={className}>{props.date}</td>;
};
